import { Card } from "@devhop/ui";
import { DollarSign, FileStackIcon, FileText, Receipt } from "lucide-react";
import type { DocumentsCountSummary } from "../lib/documents-count-summary";

const DEFAULT_SUMMARY_GRID =
  "grid grid-cols-2 gap-4 sm:grid-cols-4";

type DocumentsCountSummarySectionProps = {
  summary: DocumentsCountSummary;
  /** ຄ່າເລີ່ມ: 4 ຄໍລຳ desktop — dashboard ໃຊ້ grid-cols-2 (2×2) */
  gridClassName?: string;
  /** padding ກາດ — dashboard ສົ່ງ cardPad */
  cardClassName?: string;
};

function CountStatCard({
  label,
  value,
  hint,
  icon: Icon,
  iconClassName,
  className,
}: {
  label: string;
  value: number;
  hint?: string;
  icon: typeof FileStackIcon;
  iconClassName: string;
  className?: string;
}) {
  return (
    <Card className={className ?? "p-4"}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-muted-foreground text-xs">{label}</p>
          <p className="mt-1 font-bold text-2xl tabular-nums leading-none">
            {value.toLocaleString("lo-LA")}
          </p>
          {hint ? (
            <p className="mt-1 text-muted-foreground text-xs leading-snug">
              {hint}
            </p>
          ) : null}
        </div>
        <Icon className={`h-5 w-5 shrink-0 ${iconClassName}`} />
      </div>
    </Card>
  );
}

export function DocumentsCountSummarySection({
  summary,
  gridClassName,
  cardClassName,
}: DocumentsCountSummarySectionProps) {
  return (
    <div className={gridClassName ?? DEFAULT_SUMMARY_GRID}>
      <CountStatCard
        className={cardClassName}
        label="ເອກະສານທັງໝົດ"
        value={summary.totalDocuments}
        hint="Q / I / R"
        icon={FileStackIcon}
        iconClassName="text-slate-500"
      />
      <CountStatCard
        className={cardClassName}
        label="ໃບສະເໜີລາຄາ"
        value={summary.quotationCount}
        hint="ອອກແລ້ວ / ຊຳລະບາງສ່ວນ"
        icon={DollarSign}
        iconClassName="text-blue-500"
      />
      <CountStatCard
        className={cardClassName}
        label="ໃບແຈ້ງໜີ້"
        value={summary.invoiceCount}
        hint="ຄ້າງຊຳລະ"
        icon={FileText}
        iconClassName="text-amber-500"
      />
      <CountStatCard
        className={cardClassName}
        label="ໃບຮັບເງິນ"
        value={summary.receiptCount}
        icon={Receipt}
        iconClassName="text-emerald-500"
      />
    </div>
  );
}

export function DocumentsCountSummarySectionSkeleton({
  gridClassName,
  cardClassName,
}: {
  gridClassName?: string;
  cardClassName?: string;
} = {}) {
  const skeletonPad = cardClassName ?? "p-4";
  return (
    <div className={gridClassName ?? DEFAULT_SUMMARY_GRID}>
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={`animate-pulse rounded-xl border bg-card ${skeletonPad}`}
        >
          <div className="h-3 w-24 rounded bg-muted" />
          <div className="mt-3 h-8 w-16 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}
